import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  ProjectActivityFeedResponse,
  Task as ProjectTask,
  TaskActivity,
  TaskMessage,
} from '@claude-code-api/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  ExternalLink,
  Loader2,
  PanelRightClose,
  PanelRightOpen,
  Play,
  Square,
  TerminalSquare,
  UserRound,
  XCircle,
} from 'lucide-react';
import {
  DEFAULT_TASK_STATUS_VISIBILITY,
  useProjectTasksBoardStore,
  type TaskStatusVisibility,
  type TaskStatusVisibilityKey,
} from '@/stores/projectTasksBoardStore';
import { toast } from 'sonner';

type BoardStatus = 'pending' | 'working' | 'blocked' | 'finished';
type BoardPriority = 'low' | 'medium' | 'high' | 'urgent';

type BoardTask = {
  id: string;
  rootTaskId: string;
  parentTaskId: string | null;
  rawStatus: string;
  title: string;
  description: string;
  status: BoardStatus;
  priority: BoardPriority;
  assigneeName: string;
  labels: string[];
  updatedAtLabel: string;
  updatedAtMs: number;
  createdAtMs: number;
  startedAtMs: number | null;
  completedAtMs: number | null;
  result: string;
  failureReason: string | null;
  blockedReason: string | null;
  dependencyIds: string[];
  hasDependencyWait: boolean;
  sequenceIndex: number;
  progressPercent: number;
  estimatedHours: number;
  planContainerOnly: boolean;
};

type ObjectiveLane = {
  rootTaskId: string;
  rootTask: BoardTask;
  tasks: BoardTask[];
  active: number;
  pending: number;
  blocked: number;
  finished: number;
};

interface ProjectTasksResponse {
  tasks: ProjectTask[];
  total: number;
  byStatus: Record<string, number>;
  hasMore: boolean;
}

interface ProjectTaskDetailResponse {
  task: ProjectTask;
  rootTask?: ProjectTask | null;
  messages: TaskMessage[];
  subtasks: ProjectTask[];
  relatedTasks: ProjectTask[];
  activities: TaskActivity[];
  canClaim: boolean;
}

type TaskExecutionStartResponse = {
  task?: ProjectTask | null;
  coderSessionId?: string | null;
};

type TaskMergeResponse = {
  success?: boolean;
  task?: ProjectTask | null;
  coderSessionId?: string | null;
  mergeResult?: {
    status?: string;
    message?: string;
  } | null;
};

type TaskPreviewResponse = {
  success?: boolean;
  task?: ProjectTask | null;
  coderSessionId?: string | null;
  previewUrl?: string | null;
  openTarget?: string | null;
  previewRuntimeId?: string | null;
};

type PmBrief = {
  summary?: string;
  audit?: {
    userRequest?: string;
    interpretedGoal?: string;
    assumptions?: string[];
    constraints?: string[];
    risks?: string[];
    blockers?: string[];
    confidence?: string;
  };
  execution?: {
    expectedTouchpoints?: string[];
    filesLikelyTouched?: string[];
    validationPlan?: string[];
    successCriteria?: string[];
    requiresPreviewCheck?: boolean;
    requiresPlaywright?: boolean;
    requiresMigration?: boolean;
    requiresCommit?: boolean;
  };
  taskSplitMarkdown?: string;
  splitPlanMarkdown?: string;
  relatedTasks?: Array<{
    title?: string;
    relationship?: string;
    reason?: string;
  }>;
  userVisiblePlan?: {
    shortLabel?: string;
    whatWillChange?: string[];
    whatWillNotChange?: string[];
    doneWhen?: string[];
  };
};

type CodingSessionHealthResponse = {
  success?: boolean;
  codingSession?: {
    sessionId?: string | null;
    status?: string | null;
    health?: string | null;
    previewUrl?: string | null;
    previewUrls?: {
      port3000?: string | null;
      port5173?: string | null;
    };
  } | null;
};

interface ProjectTasksTabProps {
  projectId: string;
  layout?: 'standalone' | 'embedded';
}

type TaskListStageKey = TaskStatusVisibilityKey;

const TASK_LIST_STAGE_STYLE: Record<
  TaskListStageKey,
  {
    pill: string;
    surface: string;
    accent: string;
  }
> = {
  pending: {
    pill: 'border-border/70 bg-muted text-muted-foreground',
    surface: 'border-border/70 bg-card/95',
    accent: 'bg-muted-foreground/60',
  },
  progressing: {
    pill: 'border-warning/30 bg-warning/10 text-warning',
    surface: 'border-warning/20 bg-card/95',
    accent: 'bg-warning',
  },
  done: {
    pill: 'border-success/30 bg-success/10 text-success',
    surface: 'border-success/20 bg-card/95',
    accent: 'bg-success',
  },
  failed: {
    pill: 'border-destructive/30 bg-destructive/10 text-destructive',
    surface: 'border-destructive/20 bg-card/95',
    accent: 'bg-destructive',
  },
  other: {
    pill: 'border-primary/20 bg-primary/10 text-primary',
    surface: 'border-primary/15 bg-card/95',
    accent: 'bg-primary/80',
  },
};

function getTaskListStageMeta(stage: TaskListStageKey, t: TFunction) {
  return {
    ...TASK_LIST_STAGE_STYLE[stage],
    label: t(`projects:tasks.stages.${stage}.label`),
    description: t(`projects:tasks.stages.${stage}.description`),
  };
}

function priorityVariant(
  priority: BoardPriority,
): 'destructive' | 'warning' | 'default' | 'secondary' {
  if (priority === 'urgent') return 'destructive';
  if (priority === 'high') return 'warning';
  if (priority === 'medium') return 'default';
  return 'secondary';
}

function relativeTime(
  dateLike: string | Date | null | undefined,
  t: TFunction,
): string {
  if (!dateLike) return t('projects:tasks.time.justNow');
  const date = typeof dateLike === 'string' ? new Date(dateLike) : dateLike;
  const diff = Date.now() - date.getTime();
  if (!Number.isFinite(diff) || diff <= 0)
    return t('projects:tasks.time.justNow');
  if (diff < 60_000) return t('projects:tasks.time.justNow');
  if (diff < 3_600_000) {
    return t('projects:tasks.time.minutesAgo', {
      count: Math.floor(diff / 60_000),
    });
  }
  if (diff < 86_400_000) {
    return t('projects:tasks.time.hoursAgo', {
      count: Math.floor(diff / 3_600_000),
    });
  }
  return t('projects:tasks.time.daysAgo', {
    count: Math.floor(diff / 86_400_000),
  });
}

function formatAbsoluteTime(dateLike: string | Date | null | undefined) {
  if (!dateLike) return '';
  const date = typeof dateLike === 'string' ? new Date(dateLike) : dateLike;
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function markdownPreview(content: string): string {
  return String(content || '')
    .replace(/```[\s\S]*?```/g, ' code block ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function truncateTaskSummary(content: string, maxLength = 180): string {
  const preview = markdownPreview(content);
  if (preview.length <= maxLength) return preview;
  return `${preview.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
}

function getSubtaskProcessState(
  task: ProjectTask,
  t: TFunction,
): {
  label: string;
  tone: string;
  line: string;
  icon: ReactNode;
} {
  if (task.status === 'completed') {
    return {
      label: t('projects:tasks.process.done'),
      tone: 'text-emerald-600',
      line: 'bg-emerald-500/35',
      icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
    };
  }
  if (task.status === 'failed' || task.status === 'cancelled') {
    return {
      label:
        task.status === 'cancelled'
          ? t('projects:tasks.process.cancelled')
          : t('projects:tasks.process.failed'),
      tone: 'text-destructive',
      line: 'bg-destructive/35',
      icon: <XCircle className="h-4 w-4 text-destructive" />,
    };
  }
  if (task.status === 'assigned' || task.status === 'in_progress') {
    return {
      label: t('projects:tasks.process.processing'),
      tone: 'text-amber-600',
      line: 'bg-amber-500/35',
      icon: <Loader2 className="h-4 w-4 animate-spin text-amber-500" />,
    };
  }
  return {
    label:
      task.status === 'blocked'
        ? t('projects:tasks.process.blocked')
        : t('projects:tasks.process.waiting'),
    tone: 'text-muted-foreground',
    line: 'bg-border',
    icon: <Circle className="h-4 w-4 text-muted-foreground" />,
  };
}

function readStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
}

function readPmBrief(task: ProjectTask): PmBrief | null {
  const metadata = (task.metadata || {}) as Record<string, unknown>;
  const brief = metadata.pmBrief;
  return brief && typeof brief === 'object' && !Array.isArray(brief)
    ? (brief as PmBrief)
    : null;
}

function readPmGraphNode(task: ProjectTask): Record<string, unknown> | null {
  const metadata = (task.metadata || {}) as Record<string, unknown>;
  const node = metadata.pmGraphNode;
  return node && typeof node === 'object' && !Array.isArray(node)
    ? (node as Record<string, unknown>)
    : null;
}

function parseTaskResult(task: ProjectTask, t: TFunction): string {
  if (typeof task.result === 'string' && task.result.trim()) return task.result;
  const metadata = (task.metadata || {}) as Record<string, unknown>;
  const blockedReason =
    typeof metadata.blockedReason === 'string' ? metadata.blockedReason : null;
  if (blockedReason) return blockedReason;
  return task.status === 'completed'
    ? t('projects:tasks.result.completed')
    : t('projects:tasks.result.streaming');
}

function readTaskFailureReason(task: ProjectTask): string {
  return String(task.failure?.reason || '').trim();
}

function readDependencyIds(task: ProjectTask): string[] {
  const metadata = (task.metadata || {}) as Record<string, unknown>;
  return Array.isArray(metadata.dependsOnTaskIds)
    ? metadata.dependsOnTaskIds
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    : [];
}

function mapTaskStatus(task: ProjectTask): BoardStatus {
  const dependencyIds = readDependencyIds(task);
  if (task.status === 'completed') return 'finished';
  if (task.status === 'failed' || task.status === 'cancelled') return 'blocked';
  if (task.status === 'in_progress' || task.status === 'assigned')
    return 'working';
  if (task.status === 'waiting') return 'pending';
  if (task.status === 'blocked' && dependencyIds.length > 0) return 'pending';
  if (task.status === 'blocked') return 'blocked';
  return 'pending';
}

function mapTaskPriority(priority: number): BoardPriority {
  if (priority >= 5) return 'urgent';
  if (priority >= 4) return 'high';
  if (priority >= 3) return 'medium';
  return 'low';
}

function getTaskListStage(task: BoardTask): TaskListStageKey {
  if (task.rawStatus === 'completed') return 'done';
  if (task.rawStatus === 'failed') return 'failed';
  if (task.rawStatus === 'cancelled') return 'other';
  if (task.rawStatus === 'in_progress' || task.rawStatus === 'assigned')
    return 'progressing';
  if (task.rawStatus === 'blocked' && !task.hasDependencyWait) return 'failed';
  if (
    task.rawStatus === 'waiting' ||
    task.rawStatus === 'queued' ||
    task.rawStatus === 'pending' ||
    task.rawStatus === 'todo'
  )
    return 'pending';
  if (task.status === 'pending') return 'pending';
  if (task.status === 'working') return 'progressing';
  if (task.status === 'finished') return 'done';
  if (task.status === 'blocked') return 'failed';
  return 'other';
}

function estimateTaskHours(
  task: ProjectTask,
  metadata: Record<string, unknown>,
): number {
  const explicit = Number(metadata.estimatedHours);
  if (Number.isFinite(explicit) && explicit > 0) {
    return Math.max(1, Math.min(80, Math.round(explicit * 2) / 2));
  }

  const acceptanceCount = Array.isArray(task.acceptanceCriteria)
    ? task.acceptanceCriteria.length
    : 0;
  const descriptionLength = String(
    task.description || task.prompt || task.title || '',
  ).trim().length;
  const priorityWeight = Math.max(0, Number(task.priority || 3) - 2);
  const dependencyWeight = readDependencyIds(task).length;
  const heuristic =
    2 +
    acceptanceCount * 1.5 +
    priorityWeight +
    dependencyWeight * 0.5 +
    Math.min(4, descriptionLength / 220);
  return Math.max(1, Math.min(16, Math.round(heuristic * 2) / 2));
}

function buildBoardFromApi(tasks: ProjectTask[], t: TFunction) {
  const boardTasks = tasks.map((task) => {
    const metadata = (task.metadata || {}) as Record<string, unknown>;
    const planContainerOnly =
      metadata.planContainerOnly === true ||
      (metadata.autoPlanned === true &&
        Array.isArray(metadata.plannedSubtaskIds));
    const dependencyIds = readDependencyIds(task);
    const blockedReason =
      typeof metadata.blockedReason === 'string'
        ? metadata.blockedReason
        : null;
    const plannerStepIndex = Number.isFinite(Number(metadata.plannerStepIndex))
      ? Number(metadata.plannerStepIndex)
      : null;

    return {
      id: task.id,
      rootTaskId: task.rootTaskId || task.id,
      parentTaskId: task.parentTaskId || null,
      rawStatus: task.status,
      title:
        task.title || task.prompt || t('projects:tasks.labels.untitledTask'),
      description:
        task.description || task.prompt || t('projects:tasks.labels.noDetail'),
      status: mapTaskStatus(task),
      priority: mapTaskPriority(task.priority),
      assigneeName:
        task.agentName || task.assignedTo || t('projects:tasks.labels.aiTeam'),
      labels: [task.phase.replace(/_/g, ' ')],
      updatedAtLabel: relativeTime(task.updatedAt, t),
      updatedAtMs: new Date(task.updatedAt).getTime(),
      createdAtMs: new Date(task.createdAt).getTime(),
      startedAtMs: task.startedAt ? new Date(task.startedAt).getTime() : null,
      completedAtMs: task.completedAt
        ? new Date(task.completedAt).getTime()
        : null,
      result: parseTaskResult(task, t),
      failureReason: readTaskFailureReason(task) || null,
      blockedReason,
      dependencyIds,
      hasDependencyWait:
        dependencyIds.length > 0 &&
        (task.status === 'blocked' ||
          task.status === 'waiting' ||
          task.status === 'queued'),
      sequenceIndex: plannerStepIndex ?? -1,
      progressPercent: Number(task.progressPercent || 0),
      estimatedHours: estimateTaskHours(task, metadata),
      planContainerOnly,
    } satisfies BoardTask;
  });

  const rootCandidates = boardTasks.filter(
    (task) => task.parentTaskId === null || task.rootTaskId === task.id,
  );
  const lanes: ObjectiveLane[] = rootCandidates
    .map((rootTask) => {
      const children = boardTasks
        .filter(
          (task) =>
            task.rootTaskId === rootTask.rootTaskId && task.id !== rootTask.id,
        )
        .sort((left, right) => {
          if (left.sequenceIndex !== right.sequenceIndex)
            return left.sequenceIndex - right.sequenceIndex;
          return left.updatedAtMs - right.updatedAtMs;
        })
        .map((task, index) => ({
          ...task,
          sequenceIndex: task.sequenceIndex >= 0 ? task.sequenceIndex : index,
        }));

      return {
        rootTaskId: rootTask.rootTaskId,
        rootTask,
        tasks: children,
        active: children.filter((task) => task.status === 'working').length,
        pending: children.filter((task) => task.status === 'pending').length,
        blocked: children.filter((task) => task.status === 'blocked').length,
        finished: children.filter((task) => task.status === 'finished').length,
      };
    })
    .sort((left, right) => {
      const leftUpdated = Math.max(
        left.rootTask.updatedAtMs,
        ...left.tasks.map((task) => task.updatedAtMs),
      );
      const rightUpdated = Math.max(
        right.rootTask.updatedAtMs,
        ...right.tasks.map((task) => task.updatedAtMs),
      );
      return rightUpdated - leftUpdated;
    });

  const planContainerIds = new Set(
    boardTasks.filter((task) => task.planContainerOnly).map((task) => task.id),
  );
  const visibleTaskCards = boardTasks.filter((task) => {
    if (task.planContainerOnly) return true;
    if (task.parentTaskId && planContainerIds.has(task.parentTaskId))
      return false;
    return true;
  });
  const summary = {
    total: visibleTaskCards.length,
    active: visibleTaskCards.filter((task) => task.status === 'working').length,
    queued: visibleTaskCards.filter((task) => task.status === 'pending').length,
    dependencyWaiting: visibleTaskCards.filter((task) => task.hasDependencyWait)
      .length,
    blocked: visibleTaskCards.filter((task) => task.status === 'blocked')
      .length,
    finished: visibleTaskCards.filter((task) => task.status === 'finished')
      .length,
  };

  return { tasks: visibleTaskCards, lanes, summary };
}

function TaskSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="border-t border-border/60 pt-3">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {title}
      </div>
      {children}
    </section>
  );
}

function MarkdownViewer({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'prose prose-sm max-w-none text-foreground dark:prose-invert prose-headings:mb-2 prose-headings:mt-4 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-pre:my-3 prose-code:before:hidden prose-code:after:hidden',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}

function InlineMarkdown({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'prose prose-sm max-w-none dark:prose-invert prose-p:my-0 prose-ul:my-0 prose-ol:my-0 prose-li:my-0 prose-code:before:hidden prose-code:after:hidden',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}

function ActivityTimeline({
  items,
}: {
  items: Array<Pick<TaskActivity, 'id' | 'message' | 'createdAt'>>;
}) {
  const { t } = useTranslation(['projects']);
  return (
    <ol className="relative space-y-0 pl-4 text-sm">
      {items.map((activity, index) => (
        <li key={activity.id} className="relative pb-4 last:pb-0">
          {index < items.length - 1 ? (
            <span className="absolute left-[-9px] top-3 h-full w-px bg-border/70" />
          ) : null}
          <span className="absolute left-[-13px] top-1.5 h-2.5 w-2.5 rounded-full border border-background bg-muted-foreground" />
          <div className="font-medium leading-snug">{activity.message}</div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            {relativeTime(activity.createdAt, t)}
          </div>
        </li>
      ))}
    </ol>
  );
}

function TaskListBoard({
  tasks,
  selectedTaskId,
  onSelectTask,
  visibleStatuses,
}: {
  tasks: BoardTask[];
  selectedTaskId: string | null;
  onSelectTask: (taskId: string) => void;
  visibleStatuses: TaskStatusVisibility;
}) {
  const { t } = useTranslation(['projects']);
  const stageOrder = useMemo<TaskListStageKey[]>(
    () =>
      ['pending', 'progressing', 'done', 'failed', 'other'].filter(
        (stage): stage is TaskListStageKey =>
          visibleStatuses[stage as TaskListStageKey],
      ),
    [visibleStatuses],
  );
  const [collapsedTaskIds, setCollapsedTaskIds] = useState<string[]>([]);
  const [collapsedLanes, setCollapsedLanes] = useState<TaskListStageKey[]>([]);
  const tasksByStage = useMemo(
    () =>
      stageOrder.reduce<Record<TaskListStageKey, BoardTask[]>>(
        (acc, stage) => {
          acc[stage] = tasks.filter((task) => getTaskListStage(task) === stage);
          return acc;
        },
        {} as Record<TaskListStageKey, BoardTask[]>,
      ),
    [stageOrder, tasks],
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto p-5">
      {stageOrder.length === 0 ? (
        <div className="flex min-h-[16rem] items-center justify-center rounded-xl border border-dashed border-border/70 bg-background/70 p-6 text-sm text-muted-foreground">
          {t('projects:tasks.board.allHidden')}
        </div>
      ) : (
        <div className="flex min-h-0 gap-4 items-start min-w-max">
          {stageOrder.map((stage) => {
            const stageMeta = getTaskListStageMeta(stage, t);
            const stageTasks = tasksByStage[stage];
            const laneCollapsed = collapsedLanes.includes(stage);

            return (
              <div
                key={stage}
                className={cn(
                  'glass-panel flex min-h-[16rem] w-[20rem] min-w-[20rem] flex-col rounded-xl border shadow-sm',
                  stageMeta.surface,
                )}
              >
                <div className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'h-2.5 w-2.5 rounded-full',
                          stageMeta.accent,
                        )}
                      />
                      <div className="text-sm font-semibold">
                        {stageMeta.label}
                      </div>
                      <Badge
                        variant="outline"
                        className={cn('text-[11px]', stageMeta.pill)}
                      >
                        {stageTasks.length}
                      </Badge>
                    </div>
                    {!laneCollapsed ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {stageMeta.description}
                      </p>
                    ) : null}
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() =>
                      setCollapsedLanes((current) =>
                        current.includes(stage)
                          ? current.filter((item) => item !== stage)
                          : [...current, stage],
                      )
                    }
                    aria-label={t(
                      laneCollapsed
                        ? 'projects:tasks.board.expandLane'
                        : 'projects:tasks.board.collapseLane',
                      { label: stageMeta.label },
                    )}
                  >
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform',
                        laneCollapsed && '-rotate-90',
                      )}
                    />
                  </Button>
                </div>

                {!laneCollapsed ? (
                  <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
                    {stageTasks.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-border/70 bg-background/70 px-3 py-4 text-center text-xs text-muted-foreground">
                        {t('projects:tasks.board.emptyLane')}
                      </div>
                    ) : (
                      stageTasks.map((task) => {
                        const cardCollapsed = collapsedTaskIds.includes(
                          task.id,
                        );
                        return (
                          <div
                            key={task.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => onSelectTask(task.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onSelectTask(task.id);
                              }
                            }}
                            className={cn(
                              'rounded-lg border border-border/70 bg-background/95 p-3 text-left shadow-sm transition hover:border-primary/30 hover:bg-background',
                              selectedTaskId === task.id &&
                                'border-primary/40 ring-2 ring-primary/15',
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge
                                    variant={priorityVariant(task.priority)}
                                    className="capitalize"
                                  >
                                    {task.priority}
                                  </Badge>
                                  <Badge
                                    variant="outline"
                                    className="text-[11px]"
                                  >
                                    {task.labels[0]}
                                  </Badge>
                                </div>
                                <div className="mt-2 text-sm font-semibold text-balance">
                                  {markdownPreview(task.title)}
                                </div>
                                {!cardCollapsed ? (
                                  <>
                                    <div className="mt-2 text-xs text-muted-foreground">
                                      {truncateTaskSummary(
                                        task.description,
                                        180,
                                      )}
                                    </div>
                                    {task.failureReason ? (
                                      <div
                                        role="alert"
                                        className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs leading-5 text-destructive"
                                      >
                                        <div className="font-semibold">
                                          {t(
                                            'projects:tasks.sections.failureReason',
                                          )}
                                        </div>
                                        <div className="mt-1 whitespace-pre-wrap break-words">
                                          {task.failureReason}
                                        </div>
                                      </div>
                                    ) : null}
                                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted/80">
                                      <div
                                        className={cn(
                                          'h-full rounded-full transition-all',
                                          task.status === 'finished'
                                            ? 'bg-emerald-400'
                                            : task.status === 'blocked'
                                              ? 'bg-rose-400'
                                              : task.status === 'working'
                                                ? 'bg-primary'
                                                : 'bg-slate-400',
                                        )}
                                        style={{
                                          width: `${Math.max(
                                            task.progressPercent || 0,
                                            task.status === 'finished'
                                              ? 100
                                              : task.status === 'working'
                                                ? 12
                                                : 0,
                                          )}%`,
                                        }}
                                      />
                                    </div>
                                    <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                                      <span className="inline-flex items-center gap-1">
                                        <UserRound className="size-3.5" />
                                        {task.assigneeName}
                                      </span>
                                      <span>{task.updatedAtLabel}</span>
                                    </div>
                                  </>
                                ) : null}
                              </div>

                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setCollapsedTaskIds((current) =>
                                    current.includes(task.id)
                                      ? current.filter(
                                          (item) => item !== task.id,
                                        )
                                      : [...current, task.id],
                                  );
                                }}
                                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/70 bg-background text-muted-foreground"
                                aria-label={t(
                                  cardCollapsed
                                    ? 'projects:tasks.board.expandCard'
                                    : 'projects:tasks.board.collapseCard',
                                )}
                              >
                                <ChevronDown
                                  className={cn(
                                    'h-4 w-4 transition-transform',
                                    cardCollapsed && '-rotate-90',
                                  )}
                                />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TaskTableView({
  tasks,
  selectedTaskId,
  onSelectTask,
}: {
  tasks: BoardTask[];
  selectedTaskId: string | null;
  onSelectTask: (taskId: string) => void;
}) {
  const { t } = useTranslation(['projects']);
  const sortedTasks = useMemo(
    () =>
      [...tasks].sort((left, right) => {
        const stageWeight = (task: BoardTask) => {
          const stage = getTaskListStage(task);
          if (stage === 'progressing') return 0;
          if (stage === 'pending') return 1;
          if (stage === 'failed') return 2;
          if (stage === 'done') return 3;
          return 4;
        };
        const weightDelta = stageWeight(left) - stageWeight(right);
        if (weightDelta !== 0) return weightDelta;
        return right.updatedAtMs - left.updatedAtMs;
      }),
    [tasks],
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-auto border-b border-border/70 bg-background/95">
        <div className="min-w-full overflow-x-auto">
          <table className="w-full min-w-[64rem] border-collapse text-left">
            <thead className="sticky top-0 z-10 border-b border-border/60 bg-background/95 text-[11px] uppercase tracking-[0.08em] text-muted-foreground backdrop-blur supports-[backdrop-filter]:bg-background/85">
              <tr>
                <th className="px-4 py-3 font-semibold">
                  {t('projects:tasks.table.status')}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {t('projects:tasks.table.task')}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {t('projects:tasks.table.agent')}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {t('projects:tasks.table.updated')}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {t('projects:tasks.table.priority')}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {t('projects:tasks.table.progress')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {sortedTasks.map((task) => {
                const stage = getTaskListStage(task);
                const stageMeta = getTaskListStageMeta(stage, t);
                const progressValue = Math.max(
                  task.progressPercent || 0,
                  task.status === 'finished'
                    ? 100
                    : task.status === 'working'
                      ? 12
                      : 0,
                );

                return (
                  <tr
                    key={task.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectTask(task.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelectTask(task.id);
                      }
                    }}
                    className={cn(
                      'cursor-pointer transition hover:bg-muted/20',
                      selectedTaskId === task.id && 'bg-primary/10',
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'h-2 w-2 rounded-full',
                            stageMeta.accent,
                          )}
                        />
                        <span
                          className={cn(
                            'text-[11px] uppercase tracking-[0.08em]',
                            stageMeta.pill,
                            'border-none bg-transparent px-0 py-0',
                          )}
                        >
                          {stageMeta.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">
                          {markdownPreview(task.title)}
                        </div>
                        <div className="mt-1 truncate text-[11px] text-muted-foreground">
                          {truncateTaskSummary(task.description, 120)}
                        </div>
                        {task.failureReason ? (
                          <div
                            role="alert"
                            className="mt-2 max-w-xl rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2 text-xs leading-5 text-destructive"
                          >
                            <span className="font-semibold">
                              {t('projects:tasks.sections.failureReason')}:{' '}
                            </span>
                            <span className="whitespace-pre-wrap break-words">
                              {task.failureReason}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {task.assigneeName}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {task.updatedAtLabel}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={priorityVariant(task.priority)}
                        className="capitalize"
                      >
                        {task.priority}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex min-w-[9rem] items-center gap-3">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted/80">
                          <div
                            className={cn(
                              'h-full rounded-full',
                              task.status === 'finished'
                                ? 'bg-emerald-400'
                                : task.status === 'blocked'
                                  ? 'bg-rose-400'
                                  : task.status === 'working'
                                    ? 'bg-primary'
                                    : 'bg-slate-400',
                            )}
                            style={{ width: `${progressValue}%` }}
                          />
                        </div>
                        <span className="text-[11px] font-medium text-muted-foreground">
                          {progressValue}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TaskViewToggle({
  value,
  onChange,
}: {
  value: 'board' | 'list';
  onChange: (value: 'board' | 'list') => void;
}) {
  const { t } = useTranslation(['projects']);
  return (
    <Tabs
      value={value}
      onValueChange={(next) => onChange(next as 'board' | 'list')}
    >
      <TabsList className="h-8 rounded-full border border-border/60 bg-muted/30 p-0.5 shadow-none">
        <TabsTrigger
          value="board"
          className="min-w-[5rem] rounded-full px-3 text-xs font-medium text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-none"
        >
          {t('projects:tasks.view.board')}
        </TabsTrigger>
        <TabsTrigger
          value="list"
          className="min-w-[5rem] rounded-full px-3 text-xs font-medium text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-none"
        >
          {t('projects:tasks.view.list')}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}

function CodingExecutionDialog({
  open,
  onOpenChange,
  task,
  messages,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: ProjectTask | null;
  messages: TaskMessage[];
  loading: boolean;
}) {
  const { t } = useTranslation(['projects']);
  const fallbackTranscript = useMemo(() => {
    if (!task) return '';
    const errorMessage = readTaskFailureReason(task);
    if (errorMessage) return errorMessage;
    return String(task.result || '').trim();
  }, [task]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-border/70 px-5 py-4">
          <DialogTitle className="flex items-center gap-2">
            <TerminalSquare className="h-4 w-4" />
            {t('projects:tasks.executionDialog.title')}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {task?.title ||
                  task?.prompt ||
                  t('projects:tasks.executionDialog.noActiveTask')}
              </span>
              {task?.phase ? (
                <Badge variant="outline" className="rounded-full capitalize">
                  {task.phase}
                </Badge>
              ) : null}
              {task?.status ? (
                <Badge variant="outline" className="rounded-full capitalize">
                  {task.status.replace(/_/g, ' ')}
                </Badge>
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('projects:tasks.executionDialog.loading')}
            </div>
          ) : messages.length > 0 ? (
            <div className="space-y-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className="overflow-hidden rounded-xl border border-border/70 bg-muted/20"
                >
                  <div className="flex flex-wrap items-center gap-2 border-b border-border/60 px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <span>{message.role}</span>
                    {message.type ? <span>{message.type}</span> : null}
                    {message.authorName ? (
                      <span>{message.authorName}</span>
                    ) : null}
                    <span className="ml-auto normal-case tracking-normal">
                      {formatAbsoluteTime(message.createdAt)}
                    </span>
                  </div>
                  <pre className="overflow-x-auto whitespace-pre-wrap break-words px-3 py-3 font-mono text-xs leading-5 text-foreground">
                    {message.content}
                  </pre>
                </div>
              ))}
            </div>
          ) : fallbackTranscript ? (
            <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/20">
              <div className="flex flex-wrap items-center gap-2 border-b border-border/60 px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                <span>
                  {task?.status === 'failed'
                    ? t('projects:tasks.executionDialog.failure')
                    : t('projects:tasks.executionDialog.result')}
                </span>
                <span className="ml-auto normal-case tracking-normal">
                  {task?.updatedAt ? formatAbsoluteTime(task.updatedAt) : ''}
                </span>
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap break-words px-3 py-3 font-mono text-xs leading-5 text-foreground">
                {fallbackTranscript}
              </pre>
            </div>
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              {t('projects:tasks.executionDialog.empty')}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProjectTaskInspector({
  selectedTask,
  rootTask,
  messages,
  activities,
  relatedTasks,
  subtasks,
  recentActivity,
  drawerTab,
  onDrawerTabChange,
  onSelectTask,
  onToggleCollapse,
  onStartTask,
  onOpenPreview,
  onStopPreview,
  onMergeTask,
  startingTask,
  openingPreview,
  stoppingPreview,
  mergingTask,
}: {
  selectedTask: ProjectTask | null;
  rootTask: ProjectTask | null;
  messages: TaskMessage[];
  activities: TaskActivity[];
  relatedTasks: ProjectTask[];
  subtasks: ProjectTask[];
  recentActivity: TaskActivity[];
  drawerTab: 'info' | 'actions' | 'activity';
  onDrawerTabChange: (tab: 'info' | 'actions' | 'activity') => void;
  onSelectTask: (taskId: string) => void;
  onToggleCollapse: () => void;
  onStartTask: (taskId: string) => void;
  onOpenPreview: (taskId: string) => void;
  onStopPreview: (taskId: string) => void;
  onMergeTask: (taskId: string) => void;
  startingTask: boolean;
  openingPreview: boolean;
  stoppingPreview: boolean;
  mergingTask: boolean;
}) {
  const { t } = useTranslation(['projects']);
  const [expandedGraphTaskIds, setExpandedGraphTaskIds] = useState<string[]>(
    [],
  );

  if (!selectedTask) {
    return (
      <div className="relative rounded-xl border border-border/70 bg-background p-5 shadow-sm">
        <div className="text-lg font-semibold">
          {t('projects:tasks.inspector.emptyTitle')}
        </div>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="sticky right-4 top-4 z-10 float-right -mt-1 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/95 text-muted-foreground shadow-sm backdrop-blur transition-colors hover:bg-muted hover:text-foreground"
          aria-label={t('projects:tasks.inspector.collapseDetail')}
        >
          <PanelRightClose className="h-4 w-4" />
        </button>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('projects:tasks.inspector.emptyDescription')}
        </p>
      </div>
    );
  }

  const detailOverview = selectedTask.description || selectedTask.prompt || '';
  const pmBrief = readPmBrief(selectedTask);
  const pmGraphNode = readPmGraphNode(selectedTask);
  const validationPlan =
    readStringList(pmGraphNode?.validationPlan).length > 0
      ? readStringList(pmGraphNode?.validationPlan)
      : readStringList(pmBrief?.execution?.validationPlan);
  const likelyFiles =
    readStringList(pmGraphNode?.filesLikelyTouched).length > 0
      ? readStringList(pmGraphNode?.filesLikelyTouched)
      : readStringList(pmBrief?.execution?.filesLikelyTouched);
  const successCriteria = readStringList(pmBrief?.execution?.successCriteria);
  const pmRelatedTasks = Array.isArray(pmBrief?.relatedTasks)
    ? pmBrief.relatedTasks.filter((task) =>
        String(task?.title || task?.reason || '').trim(),
      )
    : [];
  const dependencyIds = readDependencyIds(selectedTask);
  const detailResult =
    typeof selectedTask.result === 'string' ? selectedTask.result.trim() : '';
  const failureReason =
    selectedTask.status === 'failed' ? readTaskFailureReason(selectedTask) : '';
  const executionMode =
    selectedTask.execution?.mode === 'auto' ? 'auto' : 'manual';
  const canStartTask = selectedTask.execution?.canStart === true;
  const coderSessionId =
    String(selectedTask.execution?.coderSessionId || '').trim() || null;
  const ownerSessionId =
    String(selectedTask.execution?.ownerSessionId || '').trim() || null;
  const isPlanContainerTask =
    selectedTask.metadata?.planContainerOnly === true ||
    selectedTask.assignedTo === 'pm';
  const mergeStatus = String(selectedTask.merge?.status || '')
    .trim()
    .toLowerCase();
  const reviewerProducedChanges =
    selectedTask.assignedTo === 'reviewer' &&
    (selectedTask.metadata?.reviewerUnexpectedChanges === true ||
      selectedTask.metadata?.branchHasChanges === true ||
      (Array.isArray(selectedTask.metadata?.branchChangedFiles) &&
        selectedTask.metadata.branchChangedFiles.length > 0));
  const showMergeStatus =
    !isPlanContainerTask && Boolean(mergeStatus) && mergeStatus !== 'idle';
  const canMergeTask =
    !isPlanContainerTask &&
    ['ready', 'conflicted', 'failed'].includes(mergeStatus);
  const mergeBlockedReason = selectedTask.merge?.blockedReason || null;
  const previewStatus = String(selectedTask.preview?.status || '')
    .trim()
    .toLowerCase();
  const previewUrl = String(selectedTask.preview?.url || '').trim() || null;
  const previewRuntimeId =
    String(selectedTask.preview?.runtimeId || '').trim() || null;
  const showTaskPreviewAction = selectedTask.status === 'completed';
  const canUseTaskPreview = showTaskPreviewAction;
  const canStopPreview =
    canUseTaskPreview &&
    (previewStatus === 'ready' || previewStatus === 'starting') &&
    Boolean(previewRuntimeId);
  const showAcceptanceCriteria =
    subtasks.length === 0 && Boolean(selectedTask.acceptanceCriteria?.length);
  const showExecutionAction =
    !isPlanContainerTask &&
    selectedTask.status !== 'completed' &&
    selectedTask.status !== 'cancelled';

  return (
    <div className="relative rounded-lg border border-border/70 bg-background px-4 py-4 shadow-sm">
      <button
        type="button"
        onClick={onToggleCollapse}
        className="sticky right-4 top-4 z-10 float-right -mr-1 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/95 text-muted-foreground shadow-sm backdrop-blur transition-colors hover:bg-muted hover:text-foreground"
        aria-label={t('projects:tasks.inspector.collapseDetail')}
      >
        <PanelRightClose className="h-4 w-4" />
      </button>
      <div className="space-y-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">
              {selectedTask.phase.replace(/_/g, ' ')}
            </Badge>
            <Badge
              variant={priorityVariant(mapTaskPriority(selectedTask.priority))}
            >
              {mapTaskPriority(selectedTask.priority)}
            </Badge>
            <Badge variant="secondary">
              {selectedTask.agentName ||
                selectedTask.assignedTo ||
                t('projects:tasks.labels.aiTeam')}
            </Badge>
            <Badge variant="outline">
              {executionMode === 'auto'
                ? t('projects:tasks.executionMode.auto')
                : t('projects:tasks.executionMode.manual')}
            </Badge>
            {showMergeStatus ? (
              <Badge variant="outline">
                {t('projects:tasks.merge.statusBadge', {
                  status: mergeStatus,
                })}
              </Badge>
            ) : null}
          </div>
          <h3 className="mt-3 text-lg font-semibold text-balance">
            {markdownPreview(
              selectedTask.title ||
                selectedTask.prompt ||
                t('projects:tasks.labels.untitledTask'),
            )}
          </h3>
          {dependencyIds.length ? (
            <div className="mt-2 text-xs text-muted-foreground">
              {t('projects:tasks.dependencies.waitingOn', {
                count: dependencyIds.length,
              })}
            </div>
          ) : null}
          {reviewerProducedChanges ? (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {t('projects:tasks.merge.unexpectedReviewerChanges')}
            </div>
          ) : null}
        </div>

        <Tabs
          value={drawerTab}
          onValueChange={(value) =>
            onDrawerTabChange(value as 'info' | 'actions' | 'activity')
          }
        >
          <TabsList className="h-8 w-full rounded-full border border-border/60 bg-muted/30 p-0.5 shadow-none">
            <TabsTrigger
              value="info"
              className="flex-1 rounded-full px-3 text-xs font-medium text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              {t('projects:tasks.tabs.info')}
            </TabsTrigger>
            <TabsTrigger
              value="actions"
              className="flex-1 rounded-full px-3 text-xs font-medium text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              {t('projects:tasks.tabs.actions')}
            </TabsTrigger>
            <TabsTrigger
              value="activity"
              className="flex-1 rounded-full px-3 text-xs font-medium text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              {t('projects:tasks.tabs.activity')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {drawerTab === 'actions' ? (
          <TaskSection title={t('projects:tasks.sections.actions')}>
            <div className="divide-y divide-border/60 text-sm">
              {showExecutionAction ? (
                <div className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="font-medium">
                      {t('projects:tasks.actions.execution')}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {ownerSessionId
                        ? t('projects:tasks.actions.executionHandled')
                        : coderSessionId
                          ? executionMode === 'manual'
                            ? t('projects:tasks.actions.executionManual')
                            : t('projects:tasks.actions.executionAuto')
                          : t('projects:tasks.actions.executionUnavailable')}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => onStartTask(selectedTask.id)}
                    disabled={!canStartTask || startingTask}
                    className="shrink-0"
                  >
                    {startingTask ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="mr-2 h-4 w-4" />
                    )}
                    {t('projects:tasks.actions.startTask')}
                  </Button>
                </div>
              ) : null}

              {showTaskPreviewAction ? (
                <div className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="font-medium">
                      {t('projects:tasks.actions.preview')}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {!canUseTaskPreview
                        ? t('projects:tasks.preview.availableAfterSuccess')
                        : previewUrl
                          ? t('projects:tasks.preview.openDescription')
                          : t('projects:tasks.preview.prepareDescription')}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {canStopPreview ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => onStopPreview(selectedTask.id)}
                        disabled={stoppingPreview}
                      >
                        {stoppingPreview ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Square className="mr-2 h-4 w-4" />
                        )}
                        {t('projects:tasks.actions.stop')}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => onOpenPreview(selectedTask.id)}
                      disabled={!canUseTaskPreview || openingPreview}
                    >
                      {openingPreview ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <ExternalLink className="mr-2 h-4 w-4" />
                      )}
                      {previewStatus === 'ready'
                        ? t('projects:tasks.actions.open')
                        : t('projects:tasks.actions.prepare')}
                    </Button>
                  </div>
                </div>
              ) : null}

              {!isPlanContainerTask && selectedTask.status === 'completed' ? (
                <div className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="font-medium">
                      {t('projects:tasks.actions.merge')}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {mergeStatus === 'merged'
                        ? t('projects:tasks.merge.alreadyMerged')
                        : mergeStatus === 'ready'
                          ? t('projects:tasks.merge.ready')
                          : mergeBlockedReason ||
                            t('projects:tasks.merge.needsCleanResult')}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onMergeTask(selectedTask.id)}
                    disabled={
                      !canMergeTask || mergingTask || mergeStatus === 'merged'
                    }
                    className="shrink-0"
                  >
                    {mergingTask ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                    )}
                    {mergeStatus === 'merged'
                      ? t('projects:tasks.actions.merged')
                      : canMergeTask
                        ? t('projects:tasks.actions.merge')
                        : t('projects:tasks.actions.blocked')}
                  </Button>
                </div>
              ) : null}
            </div>
          </TaskSection>
        ) : null}

        {drawerTab === 'info' ? (
          <>
            {detailOverview ? (
              <TaskSection title={t('projects:tasks.sections.taskBrief')}>
                <MarkdownViewer content={detailOverview} className="text-sm" />
              </TaskSection>
            ) : null}
            {rootTask && rootTask.id !== selectedTask.id ? (
              <TaskSection title={t('projects:tasks.sections.parentObjective')}>
                <button
                  type="button"
                  onClick={() => onSelectTask(rootTask.id)}
                  className="flex w-full items-center justify-between gap-3 text-left text-sm font-medium hover:text-primary"
                >
                  <span className="line-clamp-2">
                    {markdownPreview(
                      rootTask.title ||
                        rootTask.prompt ||
                        t('projects:tasks.labels.untitledTask'),
                    )}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </TaskSection>
            ) : null}
            {subtasks.length > 0 || relatedTasks.length > 0 ? (
              <TaskSection
                title={
                  subtasks.length > 0
                    ? t('projects:tasks.sections.subtasks')
                    : t('projects:tasks.sections.taskGraph')
                }
              >
                <div className="text-sm">
                  {[...subtasks.slice(0, 12), ...relatedTasks.slice(0, 4)]
                    .filter(
                      (task, index, list) =>
                        task.id !== selectedTask.id &&
                        list.findIndex((item) => item.id === task.id) === index,
                    )
                    .map((task, index, list) => {
                      const expanded = expandedGraphTaskIds.includes(task.id);
                      const description = String(
                        task.description || task.prompt || '',
                      ).trim();
                      const processState = getSubtaskProcessState(task, t);
                      const taskFailureReason = readTaskFailureReason(task);
                      const agentLabel =
                        task.status === 'assigned' ||
                        task.status === 'in_progress'
                          ? task.agentName ||
                            task.assignedTo ||
                            t('projects:tasks.labels.codingAi')
                          : task.agentName || task.assignedTo || null;
                      return (
                        <div key={task.id} className="relative flex gap-3 pb-3">
                          <div className="relative flex w-5 shrink-0 justify-center">
                            <span className="relative z-10 mt-2 flex h-5 w-5 items-center justify-center rounded-full bg-background">
                              {processState.icon}
                            </span>
                            {index < list.length - 1 ? (
                              <span
                                className={cn(
                                  'absolute left-1/2 top-7 h-[calc(100%-1.75rem)] w-px -translate-x-1/2',
                                  processState.line,
                                )}
                              />
                            ) : null}
                          </div>
                          <div className="min-w-0 flex-1 border-b border-border/50 pb-3 last:border-b-0">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedGraphTaskIds((current) =>
                                  current.includes(task.id)
                                    ? current.filter((id) => id !== task.id)
                                    : [...current, task.id],
                                )
                              }
                              className="flex w-full items-start justify-between gap-3 text-left hover:text-primary"
                            >
                              <span className="min-w-0">
                                <span className="block truncate font-medium">
                                  {markdownPreview(
                                    task.title ||
                                      task.prompt ||
                                      t('projects:tasks.labels.untitledTask'),
                                  )}
                                </span>
                                <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                                  <span
                                    className={cn(
                                      'font-semibold',
                                      processState.tone,
                                    )}
                                  >
                                    {processState.label}
                                  </span>
                                  <span>{task.phase.replace(/_/g, ' ')}</span>
                                  {agentLabel ? (
                                    <span>{markdownPreview(agentLabel)}</span>
                                  ) : null}
                                </span>
                              </span>
                              <ChevronDown
                                className={cn(
                                  'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                                  expanded && 'rotate-180',
                                )}
                              />
                            </button>
                            {taskFailureReason ? (
                              <div
                                role="alert"
                                className="mt-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs leading-5 text-destructive"
                              >
                                <div className="font-semibold">
                                  {t('projects:tasks.sections.failureReason')}
                                </div>
                                <div className="mt-1 whitespace-pre-wrap break-words">
                                  {taskFailureReason}
                                </div>
                              </div>
                            ) : null}
                            {expanded ? (
                              <div className="mt-3 space-y-3 text-sm">
                                {description ? (
                                  <MarkdownViewer
                                    content={description}
                                    className="text-sm"
                                  />
                                ) : null}
                                {task.acceptanceCriteria?.length ? (
                                  <ul className="space-y-1 text-xs text-muted-foreground">
                                    {task.acceptanceCriteria.map(
                                      (item, index) => (
                                        <li key={`${task.id}-${index}-${item}`}>
                                          - {markdownPreview(item)}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </TaskSection>
            ) : null}

            {pmRelatedTasks.length ? (
              <TaskSection title={t('projects:tasks.sections.relatedTasks')}>
                <div className="space-y-2 text-sm">
                  {pmRelatedTasks.map((task, index) => (
                    <div
                      key={`${task.title || 'related'}-${index}`}
                      className="border-l border-border/70 pl-3"
                    >
                      <div className="font-medium">
                        {task.title ||
                          t('projects:tasks.related.fallbackTitle', {
                            number: index + 1,
                          })}
                      </div>
                      <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                        {task.relationship || t('projects:tasks.related.kind')}
                      </div>
                      {task.reason ? (
                        <p className="mt-2 text-muted-foreground">
                          {task.reason}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </TaskSection>
            ) : null}

            {validationPlan.length > 0 ||
            likelyFiles.length > 0 ||
            successCriteria.length > 0 ? (
              <TaskSection
                title={t('projects:tasks.sections.executionContract')}
              >
                <div className="space-y-4 text-sm">
                  {likelyFiles.length ? (
                    <div className="border-l border-border/70 pl-3">
                      <div className="font-medium">
                        {t('projects:tasks.contract.likelyFiles')}
                      </div>
                      <ul className="mt-2 list-disc space-y-1 pl-4">
                        {likelyFiles.map((item) => (
                          <li
                            key={item}
                            className="break-words font-mono text-xs"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {validationPlan.length ? (
                    <div className="border-l border-border/70 pl-3">
                      <div className="font-medium">
                        {t('projects:tasks.contract.validationPlan')}
                      </div>
                      <ul className="mt-2 list-disc space-y-1 pl-4">
                        {validationPlan.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {successCriteria.length ? (
                    <div className="border-l border-border/70 pl-3">
                      <div className="font-medium">
                        {t('projects:tasks.contract.doneWhen')}
                      </div>
                      <ul className="mt-2 list-disc space-y-1 pl-4">
                        {successCriteria.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </TaskSection>
            ) : null}

            {showAcceptanceCriteria ? (
              <TaskSection title={t('projects:tasks.sections.acceptance')}>
                <ul className="space-y-2 text-sm text-pretty">
                  {selectedTask.acceptanceCriteria.map((item, index) => (
                    <li key={`${index}-${item}`} className="flex gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" />
                      <InlineMarkdown content={item} className="text-sm" />
                    </li>
                  ))}
                </ul>
              </TaskSection>
            ) : null}

            {failureReason ? (
              <TaskSection title={t('projects:tasks.sections.failureReason')}>
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  <MarkdownViewer
                    content={failureReason}
                    className="text-destructive"
                  />
                </div>
              </TaskSection>
            ) : null}

            {detailResult ? (
              <TaskSection
                title={
                  selectedTask.status === 'completed'
                    ? t('projects:tasks.sections.deliveryNotes')
                    : t('projects:tasks.sections.executionNotes')
                }
              >
                <MarkdownViewer content={detailResult} />
              </TaskSection>
            ) : null}
          </>
        ) : null}

        {drawerTab === 'activity' ? (
          <>
            {messages.length > 0 ? (
              <TaskSection title={t('projects:tasks.sections.transcript')}>
                <div className="space-y-2">
                  {messages.slice(-4).map((message) => (
                    <div
                      key={message.id}
                      className="border-l border-border/70 pl-3 py-1"
                    >
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        {message.role}
                      </div>
                      <MarkdownViewer
                        content={message.content}
                        className="mt-1 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </TaskSection>
            ) : null}

            {activities.length > 0 ? (
              <TaskSection
                title={t('projects:tasks.sections.recentTaskActivity')}
              >
                <ActivityTimeline items={activities.slice(0, 6)} />
              </TaskSection>
            ) : null}

            {recentActivity.length > 0 ? (
              <TaskSection title={t('projects:tasks.sections.projectPulse')}>
                <ActivityTimeline items={recentActivity.slice(0, 5)} />
              </TaskSection>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

function ProjectTasksPanel({
  projectId,
  layout = 'standalone',
}: ProjectTasksTabProps) {
  const { t } = useTranslation(['projects']);
  const queryClient = useQueryClient();
  const ensureProjectState = useProjectTasksBoardStore(
    (state) => state.ensureProjectState,
  );
  const selectedTaskId = useProjectTasksBoardStore(
    (state) => state.byProjectId[projectId]?.selectedTaskId || null,
  );
  const panelCollapsed = useProjectTasksBoardStore(
    (state) => state.byProjectId[projectId]?.panelCollapsed || false,
  );
  const activeView = useProjectTasksBoardStore(
    (state) => state.byProjectId[projectId]?.activeView || 'board',
  );
  const drawerTab = useProjectTasksBoardStore(
    (state) => state.byProjectId[projectId]?.drawerTab || 'info',
  );
  const visibleStatuses = useProjectTasksBoardStore(
    (state) =>
      state.byProjectId[projectId]?.visibleStatuses ||
      DEFAULT_TASK_STATUS_VISIBILITY,
  );
  const setSelectedTaskId = useProjectTasksBoardStore(
    (state) => state.setSelectedTaskId,
  );
  const setPanelCollapsed = useProjectTasksBoardStore(
    (state) => state.setPanelCollapsed,
  );
  const setActiveView = useProjectTasksBoardStore(
    (state) => state.setActiveView,
  );
  const setDrawerTab = useProjectTasksBoardStore((state) => state.setDrawerTab);
  const [executionDialogOpen, setExecutionDialogOpen] = useState(false);

  useEffect(() => {
    ensureProjectState(projectId);
  }, [ensureProjectState, projectId]);

  const tasksQuery = useQuery<ProjectTasksResponse>({
    queryKey: ['project-team-tasks', projectId],
    queryFn: async () => {
      const response = await fetch(
        `/api/projects/tasks/${projectId}?limit=200`,
        { credentials: 'include' },
      );
      if (!response.ok)
        throw new Error(
          t('projects:tasks.errors.loadTasksWithStatus', {
            status: response.status,
          }),
        );
      return response.json();
    },
    staleTime: 10_000,
    refetchInterval: 15_000,
  });

  const activityQuery = useQuery<ProjectActivityFeedResponse>({
    queryKey: ['project-team-activity', projectId],
    queryFn: async () => {
      const response = await fetch(`/api/projects/activity/${projectId}`, {
        credentials: 'include',
      });
      if (!response.ok)
        throw new Error(
          t('projects:tasks.errors.loadActivityWithStatus', {
            status: response.status,
          }),
        );
      return response.json();
    },
    staleTime: 5_000,
    refetchInterval: 10_000,
  });

  const detailQuery = useQuery<ProjectTaskDetailResponse>({
    queryKey: ['project-team-task-detail', projectId, selectedTaskId],
    queryFn: async () => {
      const response = await fetch(
        `/api/projects/${projectId}/tasks/${selectedTaskId}`,
        { credentials: 'include' },
      );
      if (!response.ok)
        throw new Error(
          t('projects:tasks.errors.loadDetailWithStatus', {
            status: response.status,
          }),
        );
      return response.json();
    },
    enabled: Boolean(selectedTaskId),
    staleTime: 2_000,
    refetchInterval: selectedTaskId ? 5000 : false,
  });

  const startTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await fetch(
        `/api/projects/${projectId}/tasks/${taskId}/start`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      if (response.status === 401) {
        window.location.assign('/login');
        throw new Error(t('projects:tasks.errors.sessionExpired'));
      }
      const payload = await response
        .json()
        .catch(() => ({ error: t('projects:tasks.errors.start') }));
      if (!response.ok) {
        throw new Error(
          String(
            payload?.error ||
              t('projects:tasks.errors.startWithStatus', {
                status: response.status,
              }),
          ),
        );
      }
      return payload as TaskExecutionStartResponse;
    },
    onSuccess: async (payload) => {
      const taskTitle = String(
        payload?.task?.title || payload?.task?.prompt || '',
      ).trim();
      toast.success(
        payload?.coderSessionId
          ? taskTitle
            ? t('projects:tasks.toasts.startingTaskInSession', {
                title: taskTitle,
                sessionId: payload.coderSessionId,
              })
            : t('projects:tasks.toasts.wakingInSession', {
                sessionId: payload.coderSessionId,
              })
          : taskTitle
            ? t('projects:tasks.toasts.startingTask', { title: taskTitle })
            : t('projects:tasks.toasts.wakingRequested'),
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project-team-tasks', projectId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-team-task-detail', projectId, selectedTaskId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-team-activity', projectId],
        }),
      ]);
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : t('projects:tasks.errors.start'),
      );
    },
  });

  const mergeTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await fetch(
        `/api/projects/${projectId}/tasks/${taskId}/merge`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      const payload = await response
        .json()
        .catch(() => ({ error: t('projects:tasks.errors.merge') }));
      if (!response.ok) {
        throw new Error(
          String(
            payload?.error ||
              payload?.mergeResult?.message ||
              t('projects:tasks.errors.mergeWithStatus', {
                status: response.status,
              }),
          ),
        );
      }
      return payload as TaskMergeResponse;
    },
    onSuccess: async (payload) => {
      const mergeMessage = String(payload?.mergeResult?.message || '').trim();
      toast.success(mergeMessage || t('projects:tasks.toasts.mergeCompleted'));
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project-team-tasks', projectId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-team-task-detail', projectId, selectedTaskId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-team-activity', projectId],
        }),
      ]);
    },
    onError: async (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : t('projects:tasks.errors.merge'),
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project-team-tasks', projectId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-team-task-detail', projectId, selectedTaskId],
        }),
      ]);
    },
  });

  const previewTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await fetch(
        `/api/projects/${projectId}/tasks/${taskId}/preview`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      const payload = await response
        .json()
        .catch(() => ({ error: t('projects:tasks.errors.openPreview') }));
      if (!response.ok) {
        throw new Error(
          String(
            payload?.error ||
              t('projects:tasks.errors.openPreviewWithStatus', {
                status: response.status,
              }),
          ),
        );
      }
      return payload as TaskPreviewResponse;
    },
    onSuccess: async (payload) => {
      const target = String(
        payload?.openTarget || payload?.previewUrl || '',
      ).trim();
      if (target) {
        window.open(target, '_blank', 'noopener,noreferrer');
      }
      toast.success(
        target
          ? t('projects:tasks.toasts.previewOpened')
          : t('projects:tasks.toasts.previewPrepared'),
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project-team-tasks', projectId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-team-task-detail', projectId, selectedTaskId],
        }),
      ]);
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : t('projects:tasks.errors.openPreview'),
      );
    },
  });

  const stopPreviewTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await fetch(
        `/api/projects/${projectId}/tasks/${taskId}/preview/stop`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      const payload = await response
        .json()
        .catch(() => ({ error: t('projects:tasks.errors.stopPreview') }));
      if (!response.ok) {
        throw new Error(
          String(
            payload?.error ||
              t('projects:tasks.errors.stopPreviewWithStatus', {
                status: response.status,
              }),
          ),
        );
      }
      return payload as TaskPreviewResponse;
    },
    onSuccess: async () => {
      toast.success(t('projects:tasks.toasts.previewStopped'));
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project-team-tasks', projectId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-team-task-detail', projectId, selectedTaskId],
        }),
      ]);
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : t('projects:tasks.errors.stopPreview'),
      );
    },
  });

  const openCodingPreviewMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `/api/projects/coding-session/health/${projectId}`,
        { credentials: 'include' },
      );
      const payload = await response
        .json()
        .catch(() => ({ error: t('projects:tasks.errors.loadCodingPreview') }));
      if (!response.ok) {
        throw new Error(
          String(
            payload?.error ||
              t('projects:tasks.errors.loadCodingPreviewWithStatus', {
                status: response.status,
              }),
          ),
        );
      }
      return payload as CodingSessionHealthResponse;
    },
    onSuccess: (payload) => {
      const previewUrl = String(
        payload?.codingSession?.previewUrl ||
          payload?.codingSession?.previewUrls?.port5173 ||
          payload?.codingSession?.previewUrls?.port3000 ||
          '',
      ).trim();
      if (!previewUrl) {
        toast.info(t('projects:tasks.toasts.codingPreviewStarting'));
        return;
      }
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : t('projects:tasks.errors.openCodingPreview'),
      );
    },
  });

  const startNextTaskMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `/api/projects/tasks/start-next/${projectId}`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      if (response.status === 401) {
        window.location.assign('/login');
        throw new Error(t('projects:tasks.errors.sessionExpired'));
      }
      const payload = await response
        .json()
        .catch(() => ({ error: t('projects:tasks.errors.startNext') }));
      if (!response.ok) {
        throw new Error(
          String(
            payload?.error ||
              t('projects:tasks.errors.startNextWithStatus', {
                status: response.status,
              }),
          ),
        );
      }
      return payload as TaskExecutionStartResponse;
    },
    onSuccess: async (payload) => {
      toast.success(
        payload?.coderSessionId
          ? payload?.task?.title
            ? t('projects:tasks.toasts.startingTaskInSession', {
                title: payload.task.title,
                sessionId: payload.coderSessionId,
              })
            : t('projects:tasks.toasts.wakingInSession', {
                sessionId: payload.coderSessionId,
              })
          : payload?.task?.title
            ? t('projects:tasks.toasts.startingTask', {
                title: payload.task.title,
              })
            : t('projects:tasks.toasts.wakingNext'),
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['project-team-tasks', projectId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-team-task-detail', projectId, selectedTaskId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['project-team-activity', projectId],
        }),
      ]);
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : t('projects:tasks.errors.start'),
      );
    },
  });

  const board = useMemo(
    () =>
      buildBoardFromApi(
        Array.isArray(tasksQuery.data?.tasks) ? tasksQuery.data.tasks : [],
        t,
      ),
    [tasksQuery.data?.tasks, t],
  );
  const visibleBoardTasks = useMemo(
    () => board.tasks.filter((task) => visibleStatuses[getTaskListStage(task)]),
    [board.tasks, visibleStatuses],
  );
  const activeExecutionTask = useMemo(() => {
    const tasks = Array.isArray(tasksQuery.data?.tasks)
      ? tasksQuery.data.tasks
      : [];
    return (
      tasks.find((task) =>
        String(task.execution?.ownerSessionId || '').trim(),
      ) ||
      tasks.find(
        (task) => task.status === 'in_progress' || task.status === 'assigned',
      ) ||
      null
    );
  }, [tasksQuery.data?.tasks]);
  const projectExecutionMode = useMemo<'auto' | 'manual'>(() => {
    const firstTaskMode = tasksQuery.data?.tasks?.find(
      (task) => task.execution?.mode,
    )?.execution?.mode;
    return firstTaskMode === 'auto' ? 'auto' : 'manual';
  }, [tasksQuery.data?.tasks]);
  const canStartNextTask =
    Boolean(activeExecutionTask) ||
    board.summary.queued + board.summary.blocked > 0;
  const activeCodingTaskId = useMemo(() => {
    const activeCoderTask = board.tasks.find(
      (task) =>
        (task.assigneeName === t('projects:tasks.labels.codingAi') ||
          task.assigneeName === 'Coding AI') &&
        (task.status === 'working' || task.status === 'pending'),
    );
    return activeCoderTask?.id || activeExecutionTask?.id || selectedTaskId;
  }, [activeExecutionTask?.id, board.tasks, selectedTaskId, t]);
  const activeExecutionMutation =
    startTaskMutation.isPending || startNextTaskMutation.isPending;
  const executionDetailQuery = useQuery<ProjectTaskDetailResponse>({
    queryKey: [
      'project-team-task-execution-detail',
      projectId,
      activeCodingTaskId,
    ],
    queryFn: async () => {
      const response = await fetch(
        `/api/projects/${projectId}/tasks/${activeCodingTaskId}`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        throw new Error(
          t('projects:tasks.errors.loadCodingTaskDetailWithStatus', {
            status: response.status,
          }),
        );
      }
      return response.json();
    },
    enabled: Boolean(activeCodingTaskId) && executionDialogOpen,
    staleTime: 2_000,
    refetchInterval: executionDialogOpen && activeCodingTaskId ? 4000 : false,
  });
  const embedded = layout === 'embedded';
  const selectTask = useMemo(
    () => (taskId: string) => {
      setSelectedTaskId(projectId, taskId);
      setPanelCollapsed(projectId, false);
    },
    [projectId, setPanelCollapsed, setSelectedTaskId],
  );

  useEffect(() => {
    const allTasks = Array.isArray(tasksQuery.data?.tasks)
      ? tasksQuery.data.tasks
      : [];
    if (selectedTaskId && allTasks.some((task) => task.id === selectedTaskId))
      return;
    const firstTaskId =
      board.lanes[0]?.rootTask.id ||
      board.lanes[0]?.tasks[0]?.id ||
      board.tasks[0]?.id ||
      null;
    setSelectedTaskId(projectId, firstTaskId);
  }, [
    board.lanes,
    board.tasks,
    projectId,
    selectedTaskId,
    setSelectedTaskId,
    tasksQuery.data?.tasks,
  ]);

  useEffect(() => {
    if (selectedTaskId) setPanelCollapsed(projectId, false);
  }, [projectId, selectedTaskId, setPanelCollapsed]);

  const selectedTask =
    detailQuery.data?.task ||
    tasksQuery.data?.tasks?.find((task) => task.id === selectedTaskId) ||
    null;
  const recentActivity = activityQuery.data?.data || [];
  const relatedTasks = detailQuery.data?.relatedTasks || [];
  const subtasks = detailQuery.data?.subtasks || [];
  const messages = detailQuery.data?.messages || [];
  const activities = detailQuery.data?.activities || [];
  const rootTask = detailQuery.data?.rootTask || null;

  return (
    <div
      className={
        embedded ? 'flex h-full min-h-0 w-full flex-1 flex-col' : 'space-y-4'
      }
    >
      {!embedded && panelCollapsed ? (
        <div className="fixed right-6 top-28 z-40">
          <button
            type="button"
            onClick={() => setPanelCollapsed(projectId, false)}
            className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/95 px-3 py-2 text-sm font-medium shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/80"
          >
            <PanelRightOpen className="h-4 w-4" />
            {t('projects:tasks.inspector.openDetail')}
          </button>
        </div>
      ) : null}

      <div
        className={cn(
          'rounded-xl border border-border/70 bg-background shadow-sm',
          embedded && 'flex min-h-0 flex-1 flex-col',
        )}
      >
        <div className="flex flex-col gap-3 border-b border-border/70 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <div className="text-sm font-semibold">
              {t('projects:tasks.status.title')}
            </div>
            <div className="text-xs text-muted-foreground">
              {activeExecutionTask
                ? t('projects:tasks.status.executing', {
                    title: markdownPreview(
                      activeExecutionTask.title ||
                        activeExecutionTask.prompt ||
                        t('projects:tasks.status.currentTask'),
                    ),
                  })
                : projectExecutionMode === 'auto'
                  ? t('projects:tasks.status.idleAuto')
                  : t('projects:tasks.status.idleManual')}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => openCodingPreviewMutation.mutate()}
              disabled={openCodingPreviewMutation.isPending}
            >
              {openCodingPreviewMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="mr-2 h-4 w-4" />
              )}
              {t('projects:tasks.actions.openPreview')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                if (activeExecutionTask) {
                  toast.info(
                    t('projects:tasks.status.executing', {
                      title: markdownPreview(
                        activeExecutionTask.title ||
                          activeExecutionTask.prompt ||
                          t('projects:tasks.status.currentTask'),
                      ),
                    }),
                  );
                  return;
                }
                startNextTaskMutation.mutate();
              }}
              disabled={!canStartNextTask || activeExecutionMutation}
            >
              {activeExecutionMutation ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              {t('projects:tasks.actions.runNextGroup')}
            </Button>
          </div>
        </div>

        {tasksQuery.isLoading || tasksQuery.isError ? (
          <div className="bg-background/95 px-5 py-3">
            {tasksQuery.isLoading ? (
              <p className="text-xs text-muted-foreground">
                {t('projects:tasks.loading')}
              </p>
            ) : null}
            {tasksQuery.isError ? (
              <p className="text-xs text-destructive">
                {t('projects:tasks.errors.loadNow')}
              </p>
            ) : null}
          </div>
        ) : null}

        {embedded ? (
          <div className="flex h-full min-h-0 flex-1 flex-col border-t border-border/50">
            <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
              <div className="text-sm font-semibold">
                {t('projects:tasks.title')}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setExecutionDialogOpen(true)}
                  disabled={!activeCodingTaskId}
                >
                  <TerminalSquare className="mr-2 h-4 w-4" />
                  {t('projects:tasks.status.title')}
                </Button>
                <TaskViewToggle
                  value={activeView}
                  onChange={(value) => setActiveView(projectId, value)}
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">
              {activeView === 'board' ? (
                <TaskListBoard
                  tasks={visibleBoardTasks}
                  selectedTaskId={selectedTaskId}
                  onSelectTask={selectTask}
                  visibleStatuses={visibleStatuses}
                />
              ) : (
                <TaskTableView
                  tasks={visibleBoardTasks}
                  selectedTaskId={selectedTaskId}
                  onSelectTask={selectTask}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="min-h-[78dvh] border-t border-border/50">
            <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
              <div>
                <div className="text-sm font-semibold">
                  {t('projects:tasks.control.title')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('projects:tasks.control.description')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setExecutionDialogOpen(true)}
                  disabled={!activeCodingTaskId}
                >
                  <TerminalSquare className="mr-2 h-4 w-4" />
                  {t('projects:tasks.status.title')}
                </Button>
                <TaskViewToggle
                  value={activeView}
                  onChange={(value) => setActiveView(projectId, value)}
                />
              </div>
            </div>

            {activeView === 'board' ? (
              <TaskListBoard
                tasks={visibleBoardTasks}
                selectedTaskId={selectedTaskId}
                onSelectTask={selectTask}
                visibleStatuses={visibleStatuses}
              />
            ) : (
              <TaskTableView
                tasks={visibleBoardTasks}
                selectedTaskId={selectedTaskId}
                onSelectTask={selectTask}
              />
            )}
          </div>
        )}
      </div>

      {!panelCollapsed ? (
        <div
          className={cn(
            'fixed right-6 z-40 w-[min(440px,calc(100vw-2rem))] overflow-y-auto',
            embedded
              ? 'top-24 h-[calc(100dvh-7rem)]'
              : 'top-28 h-[calc(100dvh-8rem)]',
          )}
        >
          <ProjectTaskInspector
            selectedTask={selectedTask}
            rootTask={rootTask}
            messages={messages}
            activities={activities}
            relatedTasks={relatedTasks}
            subtasks={subtasks}
            recentActivity={recentActivity}
            drawerTab={drawerTab}
            onDrawerTabChange={(tab) => setDrawerTab(projectId, tab)}
            onSelectTask={selectTask}
            onToggleCollapse={() => setPanelCollapsed(projectId, true)}
            onStartTask={(taskId) => startTaskMutation.mutate(taskId)}
            onOpenPreview={(taskId) => previewTaskMutation.mutate(taskId)}
            onStopPreview={(taskId) => stopPreviewTaskMutation.mutate(taskId)}
            onMergeTask={(taskId) => mergeTaskMutation.mutate(taskId)}
            startingTask={activeExecutionMutation}
            openingPreview={previewTaskMutation.isPending}
            stoppingPreview={stopPreviewTaskMutation.isPending}
            mergingTask={mergeTaskMutation.isPending}
          />
        </div>
      ) : null}

      <CodingExecutionDialog
        open={executionDialogOpen}
        onOpenChange={setExecutionDialogOpen}
        task={
          executionDetailQuery.data?.task ||
          tasksQuery.data?.tasks?.find(
            (task) => task.id === activeCodingTaskId,
          ) ||
          null
        }
        messages={executionDetailQuery.data?.messages || []}
        loading={
          executionDetailQuery.isLoading || executionDetailQuery.isFetching
        }
      />
    </div>
  );
}

export function ProjectTasksTab(props: ProjectTasksTabProps) {
  return <ProjectTasksPanel {...props} />;
}
